import type { Database, Enums } from "./database_types";
export type DbCategories = Database["public"]["Tables"]["Categories"]["Row"];
export type User = Database["public"]["Tables"]["Users"]["Row"];
export type Order = Database["public"]["Tables"]["Orders"]["Row"];
export type OrderItem = Database["public"]["Tables"]["OrderItems"]["Row"];
export type Product = Database["public"]["Tables"]["Products"]["Row"];
export type Store = Database["public"]["Tables"]["Stores"]["Row"];
export type StoreAddress =
  Database["public"]["Tables"]["StoreAddresses"]["Row"];
export type DbUserAddress =
  Database["public"]["Tables"]["UserAddresses"]["Row"];
export type DbCreditCard = Database["public"]["Tables"]["Payments"]["Row"];

export interface UserAddress extends DbUserAddress {
  [key: string]: string | number | null | undefined | boolean;
}

export interface CreditCard extends DbCreditCard {
  billing_address: UserAddress | null;
  [key: string]: string | number | null | undefined | UserAddress | boolean;
}

export enum ProductStatus {
  ACTIVE = "ACTIVE",
  DRAFT = "DRAFT",
  ARCHIVE = "ARCHIVE",
  SOLDOUT = "SOLD OUT",
}

export interface AdminOrderTableData {
  name: string;
  status: string;
  total_amount: number;
  created_at: number;
}

export type AdminProductTableData = {
  id: string;
  name: string;
  status: string;
  price: number;
  mediaImage?: string;
  sales?: number;
  quantity?: number;
  last_sale?: string;
  revenue?: number;
  rating?: number;
  stock?: number;
};
// type Like = {
//   user_id: string;
// };

// type CommentWithAuthor = Comment & {
//   author: {
//     avatar_url: string;
//     username: string;
//   } | null;
// };

export type AnalyticsInterval = {
  start?: string;
  end?: string;
  alias: AnalyticsIntervalAlias;
};

export enum AnalyticsIntervalAlias {
  CUSTOM = "CUSTOM",
  TODAY = "TODAY",
  YESTERDAY = "YESTERDAY",
  LAST_7_DAYS = "7 DAY",
  LAST_30_DAYS = "30 DAY",
  LAST_90_DAYS = "90 DAY",
  LAST_YEAR = "1 YEAR",
}

export enum OrderStatus {
  PENDING = "PENDING", // Just created, waiting for payment or next step
  AWAITING_CONFIRMATION = "AWAITING_CONFIRMATION", // Waiting for suppliers to confirm items
  PARTIALLY_CONFIRMED = "PARTIALLY_CONFIRMED", // At least one supplier confirmed
  CONFIRMED = "CONFIRMED", // All suppliers accepted their items
  REJECTED = "REJECTED", // All suppliers rejected
  CANCELED = "CANCELED", // Canceled manually or auto after timeout
  IN_FULFILLMENT = "IN_FULFILLMENT", // Supplier(s) shipping or preparing
  PARTIALLY_FULFILLED = "PARTIALLY_FULFILLED", // Some supplier orders shipped/delivered
  FULFILLED = "FULFILLED", // All supplier orders delivered
  COMPLETED = "COMPLETED", // Buyer confirmed receipt, payout phase
  REFUNDED = "REFUNDED", // Full refund issued
  DISPUTED = "DISPUTED", // Stripe dispute/chargeback case
  FAILED = "FAILED", // Payment or system failure
}

export enum SupplierOrderStatus {
  PENDING = "PENDING", // Supplier has not responded yet
  PARTIALLY_ACCEPTED = "PARTIALLY_ACCEPTED", // Supplier accepted some items
  EXPIRED = "EXPIRED",
  READY_FOR_PICKUP = "READY_FOR_PICKUP",
  ACCEPTED = "ACCEPTED", // Supplier accepted all items
  REJECTED = "REJECTED", // Supplier rejected all items
  CANCELED = "CANCELED", // Canceled by buyer or system (e.g., timeout)
  SHIPPED = "SHIPPED", // Supplier marked as shipped
  DELIVERED = "DELIVERED", // Buyer or system confirmed delivery
  REFUNDED = "REFUNDED", // After full or partial refund
  DISPUTED = "DISPUTED", // Payment held or reversed due to dispute
}

export interface Customer {
  id: string;
  name: string;
  email: string;
  orderCount: number;
  totalSpent: number;
  lastOrderDate: string;
  phone_number: string;
}
export type StoreReview = {
  id: string;
  user_id: string;
  rating: number;
  comment: string;
  created_at: string;
};

// Enhanced product type that includes store name (used in search results)
export type ProductWithStoreName = Product & {
  store_name: string;
  type: "product";
};

export enum StoreStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  INCOMPLETE = "INCOMPLETE",
}

export enum StoreType {
  PRODUCER = "PRODUCER",
  DISTRIBUTER = "DISTRIBUTER",
  MANUFACTURER = "MANUFACTURER",
  INDEPENDENT_RETAILER = "INDEPENDENT_RETAILER",
}

export enum StoreTier {
  BASIC = "BASIC",
  PRO = "PRO",
  PRO_PLUS = "PRO+",
}

export enum PaymentMethod {
  CARD = "CARD",
  COD = "CASH",
  CREDIT = "CREDIT",
  ZELLE = "ZELLE",
  CHEQUE = "CHECK",
  PAYPAL = "PAYPAL",
}

export enum DeliveryMethod {
  PICKUP = "PICKUP",
  SHIPPING = "SHIPPING",
}

// Supplier order list/detail helper types
export type SupplierOrderWithOrder = {
  id: string;
  created_at: string;
  status: SupplierOrderStatus;
  subtotal: number | null;
  store_id: string;
  order_id: string;
  customer_name?: string | null;
  Orders: {
    id: string;
    order_number: string | null;
    payment_method: PaymentMethod | null;
    delivery_method?: string | null;
    shipping_address: any | null;
    customer_id: string | null;
    created_at: string;
    tax: number | null;
    shipping: number | null;
    total_amount: number | null;
  } | null;
};

export type SupplierOrderItemWithProduct = {
  id: string;
  supplier_order_id: string;
  product_id: string | null;
  quantity: number | null;
  unit_price: number | null;
  total_price: number | null;
  Products?: {
    id: string;
    name: string | null;
    sku: string | null;
  } | null;
};

export enum SubscriptionStatus {
  ACTIVE = "ACTIVE",
  PAST_DUE = "PAST_DUE",
  CANCELED = "CANCELED",
  INCOMPLETE = "INCOMPLETE",
}

export const PRODUCT_UNIT_OPTIONS: Enums<"product_unit">[] = [
  "BOX",
  "POUND",
  "PALLET",
  "DOZEN",
];

export interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  price: number;
  name: string;
  store_id: string;
  store?:
    | { stripe_account_id: string | null }
    | { stripe_account_id: string | null }[];
}

// export interface SupplierOrderInsert {
//   id: string;
//   order_id: string;
//   supplier_id: string;
//   subtotal: number;
//   stripe_account_id: string | null;
//   status: SupplierOrderStatus;
//   created_at: string;
// }
